import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Eye,
  MessageCircle,
  Send,
  Settings,
  XCircle,
} from "lucide-react";

interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  status: "pending" | "approved" | "rejected" | "disabled" | "paused";
  category: string;
  bodyPreview: string | null;
  componentTypes: string[];
  componentCount: number;
  buttonCount: number;
  headerFormat: string | null;
  approvedAt: string | null;
  submittedAt: string | null;
  rejectionReason: string | null;
}

type TemplateHeaderMode = "none" | "text" | "image" | "video" | "document";

type TemplateAuthoringComponent =
  | { type: "HEADER"; format: "TEXT"; text: string }
  | { type: "HEADER"; format: "IMAGE" | "VIDEO" | "DOCUMENT" }
  | { type: "BODY"; text: string }
  | { type: "BUTTONS"; buttons: Array<{ type: "QUICK_REPLY"; text: string }> };

const STATUS_CONFIG = {
  approved: {
    icon: CheckCircle2,
    label: "Approved",
    color: "text-[var(--color-success)]",
    bg: "bg-[var(--color-success)]/10",
    border: "border-[var(--color-success)]/20",
  },
  pending: {
    icon: Clock,
    label: "Pending Review",
    color: "text-[var(--color-warning)]",
    bg: "bg-[var(--color-warning)]/10",
    border: "border-[var(--color-warning)]/20",
  },
  rejected: {
    icon: XCircle,
    label: "Rejected",
    color: "text-[var(--color-error)]",
    bg: "bg-[var(--color-error)]/10",
    border: "border-[var(--color-error)]/20",
  },
  disabled: {
    icon: AlertTriangle,
    label: "Disabled",
    color: "text-[var(--color-muted-foreground)]",
    bg: "bg-[var(--color-surface)]",
    border: "border-[var(--color-border)]",
  },
  paused: {
    icon: Clock,
    label: "Paused",
    color: "text-[var(--color-warning)]",
    bg: "bg-[var(--color-warning)]/10",
    border: "border-[var(--color-warning)]/20",
  },
} as const;

function buildTemplateComponents(input: {
  headerMode: TemplateHeaderMode;
  headerText: string;
  bodyText: string;
  quickReplyText: string;
}): TemplateAuthoringComponent[] {
  const components: TemplateAuthoringComponent[] = [];
  const headerText = input.headerText.trim();
  const bodyText = input.bodyText.trim();
  const quickReplyText = input.quickReplyText.trim();

  if (input.headerMode === "text" && headerText) {
    components.push({ type: "HEADER", format: "TEXT", text: headerText });
  } else if (input.headerMode === "image") {
    components.push({ type: "HEADER", format: "IMAGE" });
  } else if (input.headerMode === "video") {
    components.push({ type: "HEADER", format: "VIDEO" });
  } else if (input.headerMode === "document") {
    components.push({ type: "HEADER", format: "DOCUMENT" });
  }

  components.push({ type: "BODY", text: bodyText });

  if (quickReplyText) {
    components.push({
      type: "BUTTONS",
      buttons: [{ type: "QUICK_REPLY", text: quickReplyText }],
    });
  }

  return components;
}

export default function WhatsAppTemplatesPage() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WhatsAppTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateCategory, setTemplateCategory] = useState("marketing");
  const [templateLanguage, setTemplateLanguage] = useState("en_US");
  const [headerMode, setHeaderMode] = useState<TemplateHeaderMode>("none");
  const [headerText, setHeaderText] = useState("");
  const [quickReplyText, setQuickReplyText] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingBodyText, setEditingBodyText] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [submittingTemplateId, setSubmittingTemplateId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setError(null);
      const response = await apiFetch<{ data: WhatsAppTemplate[] }>("/messaging/whatsapp/templates");
      setTemplates(response.data);
    } catch (err) {
      setTemplates([]);
      setError(err instanceof Error ? err.message : "Failed to load WhatsApp templates");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    if (!templateName.trim() || !templateBody.trim()) {
      setError("Template name and body are required");
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      const components = buildTemplateComponents({
        headerMode,
        headerText,
        bodyText: templateBody,
        quickReplyText,
      });
      const response = await apiFetch<{ data: WhatsAppTemplate }>("/messaging/whatsapp/templates", {
        method: "POST",
        body: JSON.stringify({
          name: templateName.trim(),
          category: templateCategory,
          language: templateLanguage,
          components,
        }),
      });
      setTemplates((current) => [response.data, ...current.filter((template) => template.id !== response.data.id)]);
      setTemplateName("");
      setTemplateBody("");
      setHeaderMode("none");
      setHeaderText("");
      setQuickReplyText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create WhatsApp template");
    } finally {
      setIsCreating(false);
    }
  }, [headerMode, headerText, quickReplyText, templateBody, templateCategory, templateLanguage, templateName]);

  const handleSync = useCallback(async () => {
    try {
      setIsSyncing(true);
      setError(null);
      const response = await apiFetch<{ data: WhatsAppTemplate[] }>("/messaging/whatsapp/templates/sync", {
        method: "POST",
      });
      setTemplates(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sync WhatsApp templates");
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const handleStartEdit = useCallback((template: WhatsAppTemplate) => {
    setEditingTemplateId(template.id);
    setEditingBodyText(template.bodyPreview ?? "");
    setError(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingTemplateId(null);
    setEditingBodyText("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingTemplateId || !editingBodyText.trim()) {
      setError("Template body is required");
      return;
    }

    try {
      setIsSavingEdit(true);
      setError(null);
      const response = await apiFetch<{ data: WhatsAppTemplate }>(`/messaging/whatsapp/templates/${editingTemplateId}`, {
        method: "PATCH",
        body: JSON.stringify({
          bodyText: editingBodyText.trim(),
        }),
      });

      setTemplates((current) => current.map((template) => (
        template.id === editingTemplateId ? response.data : template
      )));
      setPreviewTemplate((current) => (current?.id === editingTemplateId ? response.data : current));
      setEditingTemplateId(null);
      setEditingBodyText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update WhatsApp template");
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingBodyText, editingTemplateId]);

  const handleDelete = useCallback(async (template: WhatsAppTemplate) => {
    if (!globalThis.confirm(`Delete template ${template.name}?`)) {
      return;
    }

    try {
      setDeletingTemplateId(template.id);
      setError(null);
      await apiFetch(`/messaging/whatsapp/templates/${template.id}`, {
        method: "DELETE",
      });
      setTemplates((current) => current.filter((row) => row.id !== template.id));
      setPreviewTemplate((current) => (current?.id === template.id ? null : current));
      if (editingTemplateId === template.id) {
        setEditingTemplateId(null);
        setEditingBodyText("");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete WhatsApp template");
    } finally {
      setDeletingTemplateId(null);
    }
  }, [editingTemplateId]);

  const handleSubmitForApproval = useCallback(async (template: WhatsAppTemplate) => {
    try {
      setSubmittingTemplateId(template.id);
      setError(null);
      const response = await apiFetch<{ data: WhatsAppTemplate }>(`/messaging/whatsapp/templates/${template.id}/submit`, {
        method: "POST",
      });
      setTemplates((current) => current.map((row) => (
        row.id === template.id ? response.data : row
      )));
      setPreviewTemplate((current) => (current?.id === template.id ? response.data : current));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit WhatsApp template for approval");
    } finally {
      setSubmittingTemplateId(null);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  if (isLoading) {
    return (
      <div className="max-w-4xl space-y-4">
        <Skeleton className="h-8 w-72" />
        <SkeletonCard className="h-32" />
        {[1, 2, 3].map((index) => (
          <SkeletonCard key={index} className="h-24" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header className="flex flex-col gap-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-[var(--color-success)]" />
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">
              WhatsApp Templates
            </h1>
          </div>
          <p className="max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Browse synced WhatsApp templates across approved and non-approved states, create templates with header
            media/text and quick replies, submit templates for approval, and refresh provider truth from the mounted
            messaging lifecycle API.
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {templates.length} synced template{templates.length === 1 ? "" : "s"} ·{" "}
            {templates.filter((template) => template.status === "approved").length} approved
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/settings/messaging/whatsapp"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)]"
          >
            <Settings className="h-4 w-4" />
            Provider Settings
          </Link>
          <button
            type="button"
            onClick={() => void handleSync()}
            disabled={isSyncing}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Clock className="h-4 w-4" />
            {isSyncing ? "Syncing..." : "Sync Provider"}
          </button>
          <Link
            to="/messaging/whatsapp"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary-hover)]"
          >
            <Send className="h-4 w-4" />
            Open Broadcasts
          </Link>
        </div>
      </header>

      {error ? (
        <div className="flex items-start gap-3 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-error)]" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--color-error)]">Could not load WhatsApp templates</p>
            <p className="text-sm text-[var(--color-muted-foreground)]">{error}</p>
          </div>
        </div>
      ) : null}

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="flex items-start gap-3 rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-warning)]" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--color-foreground)]">Mounted scope</p>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              This page now mounts basic lifecycle truth for the WhatsApp channel: synced status visibility across
              approved and non-approved templates, create for BODY plus optional header media/text and quick replies,
              explicit approval submission, edit/delete for synced tenant templates, and manual provider sync.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Create Template</h2>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Mounted here: BODY template creation with optional header text/media placeholders and a quick reply button.
              Provider approval status returns through the same synced lifecycle surface.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="text-[var(--color-foreground)]">Template name</span>
              <input
                name="templateName"
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder="welcome_offer"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--color-foreground)]">Language</span>
              <select
                name="templateLanguage"
                value={templateLanguage}
                onChange={(event) => setTemplateLanguage(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              >
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="ro">Romanian</option>
                <option value="de">German</option>
                <option value="fr">French</option>
              </select>
            </label>

            <label className="space-y-2 text-sm md:col-span-2">
              <span className="text-[var(--color-foreground)]">Category</span>
              <select
                name="templateCategory"
                value={templateCategory}
                onChange={(event) => setTemplateCategory(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              >
                <option value="marketing">Marketing</option>
                <option value="utility">Utility</option>
                <option value="authentication">Authentication</option>
              </select>
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--color-foreground)]">Header block</span>
              <select
                name="headerMode"
                value={headerMode}
                onChange={(event) => setHeaderMode(event.target.value as TemplateHeaderMode)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              >
                <option value="none">No header</option>
                <option value="text">Text header</option>
                <option value="image">Image header</option>
                <option value="video">Video header</option>
                <option value="document">Document header</option>
              </select>
            </label>

            <label className="space-y-2 text-sm">
              <span className="text-[var(--color-foreground)]">Quick reply button</span>
              <input
                name="quickReplyText"
                value={quickReplyText}
                onChange={(event) => setQuickReplyText(event.target.value)}
                placeholder="Start"
                maxLength={25}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              />
            </label>

            {headerMode === "text" ? (
              <label className="space-y-2 text-sm md:col-span-2">
                <span className="text-[var(--color-foreground)]">Header text</span>
                <input
                  name="headerText"
                  value={headerText}
                  onChange={(event) => setHeaderText(event.target.value)}
                  placeholder="Limited time offer"
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
                />
              </label>
            ) : null}

            <label className="space-y-2 text-sm md:col-span-2">
              <span className="text-[var(--color-foreground)]">Body copy</span>
              <textarea
                name="templateBody"
                value={templateBody}
                onChange={(event) => setTemplateBody(event.target.value)}
                placeholder="Hello {{1}}, welcome to Stero."
                rows={4}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
              />
            </label>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isCreating}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {isCreating ? "Creating..." : "Create Template"}
            </button>
          </div>
        </div>
      </section>

      {templates.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
          <MessageCircle className="h-10 w-10 text-[var(--color-muted-foreground)]" />
          <p className="text-sm font-medium text-[var(--color-foreground)]">No synced templates yet</p>
          <p className="max-w-lg text-sm text-[var(--color-muted-foreground)]">
            Finish provider setup, sync provider truth, or create a basic body template to seed the mounted lifecycle surface.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              isEditing={editingTemplateId === template.id}
              isSavingEdit={isSavingEdit && editingTemplateId === template.id}
              isDeleting={deletingTemplateId === template.id}
              isSubmitting={submittingTemplateId === template.id}
              editingBodyText={editingTemplateId === template.id ? editingBodyText : ""}
              onEditStart={() => handleStartEdit(template)}
              onEditCancel={handleCancelEdit}
              onEditBodyChange={setEditingBodyText}
              onEditSave={() => void handleSaveEdit()}
              onDelete={() => void handleDelete(template)}
              onSubmitForApproval={() => void handleSubmitForApproval(template)}
              onPreview={() => setPreviewTemplate(template)}
            />
          ))}
        </div>
      )}

      {previewTemplate ? (
        <TemplatePreview template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      ) : null}
    </div>
  );
}

function TemplateCard({
  template,
  isEditing,
  isSavingEdit,
  isDeleting,
  isSubmitting,
  editingBodyText,
  onEditStart,
  onEditCancel,
  onEditBodyChange,
  onEditSave,
  onDelete,
  onSubmitForApproval,
  onPreview,
}: {
  template: WhatsAppTemplate;
  isEditing: boolean;
  isSavingEdit: boolean;
  isDeleting: boolean;
  isSubmitting: boolean;
  editingBodyText: string;
  onEditStart: () => void;
  onEditCancel: () => void;
  onEditBodyChange: (value: string) => void;
  onEditSave: () => void;
  onDelete: () => void;
  onSubmitForApproval: () => void;
  onPreview: () => void;
}) {
  const statusConfig = STATUS_CONFIG[template.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div className="stagger-item flex items-center gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 transition-colors hover:bg-[var(--color-surface)]">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="truncate font-medium text-[var(--color-foreground)]">{template.name}</h2>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
            <StatusIcon className="h-3 w-3" />
            {statusConfig.label}
          </span>
          <span className="inline-flex items-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[var(--color-foreground)]">
            {template.category}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[var(--color-muted-foreground)]">
          <span>Language: {template.language}</span>
          <span>{template.componentCount} block{template.componentCount === 1 ? "" : "s"}</span>
          {template.buttonCount > 0 ? <span>{template.buttonCount} button{template.buttonCount === 1 ? "" : "s"}</span> : null}
          {template.headerFormat ? <span>Header: {template.headerFormat}</span> : null}
          <span>Source: live messaging contract</span>
        </div>
        {template.bodyPreview ? (
          <p className="mt-2 line-clamp-2 text-sm text-[var(--color-muted-foreground)]">{template.bodyPreview}</p>
        ) : null}
        {template.rejectionReason ? (
          <p className="mt-2 text-sm text-[var(--color-error)]">Rejection reason: {template.rejectionReason}</p>
        ) : null}
        {isEditing ? (
          <div className="mt-3 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-3">
            <label className="block space-y-2 text-sm">
              <span className="text-[var(--color-foreground)]">Mounted BODY copy edit</span>
              <textarea
                name={`editBody-${template.id}`}
                value={editingBodyText}
                onChange={(event) => onEditBodyChange(event.target.value)}
                rows={4}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-[var(--color-foreground)]"
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onEditCancel}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-card)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onEditSave}
                disabled={isSavingEdit}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingEdit ? "Saving..." : "Save Body Copy"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {template.status !== "approved" ? (
          <button
            type="button"
            onClick={onSubmitForApproval}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-success)]/30 px-3 py-2 text-sm font-medium text-[var(--color-success)] transition-colors hover:bg-[var(--color-success)]/5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {isSubmitting ? "Submitting..." : "Submit for Approval"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onEditStart}
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)]"
        >
          Edit Body
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-error)]/20 px-3 py-2 text-sm font-medium text-[var(--color-error)] transition-colors hover:bg-[var(--color-error)]/5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
        <button
          type="button"
          onClick={onPreview}
          className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)]"
        >
          <Eye className="h-4 w-4" />
          Preview Scope
        </button>
      </div>
    </div>
  );
}

function TemplatePreview({
  template,
  onClose,
}: {
  template: WhatsAppTemplate;
  onClose: () => void;
}) {
  const statusConfig = STATUS_CONFIG[template.status];
  const StatusIcon = statusConfig.icon;

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Template Preview Scope</h2>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            The mounted API now carries synced lifecycle metadata plus stored BODY/component detail, media header
            placeholders, quick replies, and explicit approval submission state.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
        >
          <XCircle className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium text-[var(--color-foreground)]">{template.name}</h3>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border}`}>
              <StatusIcon className="h-3 w-3" />
              {statusConfig.label}
            </span>
          </div>
          <dl className="grid gap-3 text-sm">
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Language</dt>
              <dd className="text-[var(--color-foreground)]">{template.language}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Category</dt>
              <dd className="text-[var(--color-foreground)]">{template.category}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Mounted detail</dt>
              <dd className="text-[var(--color-foreground)]">
                {template.componentCount} block{template.componentCount === 1 ? "" : "s"}
                {template.componentTypes.length > 0 ? ` · ${template.componentTypes.join(", ")}` : ""}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Body preview</dt>
              <dd className="text-[var(--color-foreground)]">{template.bodyPreview ?? "No body preview stored"}</dd>
            </div>
            {template.rejectionReason ? (
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Rejection reason</dt>
                <dd className="text-[var(--color-error)]">{template.rejectionReason}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Approval timestamps</dt>
              <dd className="text-[var(--color-foreground)]">
                Submitted: {template.submittedAt ? formatDateTime(template.submittedAt) : "Unknown"}
                <br />
                Approved: {template.approvedAt ? formatDateTime(template.approvedAt) : "Unknown"}
              </dd>
            </div>
            <div>
              <dt className="text-[var(--color-muted-foreground)]">Authoring surface</dt>
              <dd className="text-[var(--color-foreground)]">
                BODY copy, optional header text/media placeholders, quick reply buttons, edit/delete, provider sync,
                and explicit approval submission.
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl bg-[var(--color-surface)] p-4">
          <div className="mx-auto max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">WhatsApp operator note</p>
            <p className="mt-3 text-sm font-medium text-[var(--color-foreground)]">{template.name}</p>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              {template.bodyPreview ?? "This template is approved and available to the live messaging workflows already mounted in the product."}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-[var(--color-primary)]">
              <span>Use in broadcasts or reply flows</span>
              <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
